import {
  memo, useEffect, useState,
} from '../../lib/teact/teact';

import type { NewKnowledgeBaseItem } from './UploadKnowledgeModal';

import buildClassName from '../../util/buildClassName';

import useLastCallback from '../../hooks/useLastCallback';

import Icon from '../common/icons/Icon';
import UploadKnowledgeModal from './UploadKnowledgeModal';

import styles from './KnowledgeBaseView.module.scss';

interface KnowledgeBaseItem {
  id: number;
  program_code: string;
  title: string;
  program_type?: string;
  status: string;
  total_chunks: number;
  source_file?: string;
}

interface ChunkItem {
  id: string;
  content: string;
  program_code: string;
  metadata?: {
    token_count?: number;
    similarity?: number;
  };
}

type FilterStatus = 'all' | 'active' | 'closed';

import { CRM_BACKEND_URL as BACKEND_URL } from '../../util/crmBackendUrl';

function getProgramTypeLabel(type?: string): string {
  switch (type) {
    case 'maestria':
      return '🏛️ Maestría';
    case 'especialidad':
      return '🩺 Especialidad';
    case 'institucional':
      return '📋 Institucional';
    case 'diplomado':
    default:
      return '🎓 Diplomado';
  }
}

const KnowledgeBaseView = () => {
  const [items, setItems] = useState<KnowledgeBaseItem[]>([]);
  const [selectedProgram, setSelectedProgram] = useState<KnowledgeBaseItem | undefined>(undefined);
  const [chunks, setChunks] = useState<ChunkItem[]>([]);
  const [filterStatus, setFilterStatus] = useState<FilterStatus>('all');
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [feedback, setFeedback] = useState<string | undefined>(undefined);
  const [droppedFile, setDroppedFile] = useState<File | undefined>(undefined);
  const [isBannerDragOver, setIsBannerDragOver] = useState(false);

  const loadData = useLastCallback(async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/knowledge-bases`);
      if (res.ok) {
        const data = await res.json();
        if (Array.isArray(data)) {
          setItems(data.filter((item): item is KnowledgeBaseItem => Boolean(item && item.program_code && item.status)));
        }
      }
    } catch {
      // Fallback
    }
  });

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleSelectProgramChunks = useLastCallback(async (prog: KnowledgeBaseItem) => {
    setSelectedProgram(prog);
    try {
      const res = await fetch(`${BACKEND_URL}/knowledge-bases/${prog.id}/chunks`);
      if (res.ok) {
        const data = await res.json();
        setChunks(data);
      }
    } catch {
      // Fallback
    }
  });

  const handleReindex = useLastCallback(async (id: number) => {
    setFeedback('Re-indexando vectores en Qdrant con BAAI/bge-m3...');
    try {
      await fetch(`${BACKEND_URL}/knowledge-bases/${id}/reindex`, { method: 'POST' });
      setFeedback('✅ Re-indexación completada exitosamente.');
      setTimeout(() => setFeedback(undefined), 3500);
      loadData();
    } catch {
      setFeedback('Error al re-indexar');
      setTimeout(() => setFeedback(undefined), 3000);
    }
  });

  const handleDeleteProgram = useLastCallback(async (item: KnowledgeBaseItem) => {
    const confirmed = window.confirm(
      `¿Estás seguro de eliminar el documento del programa "${item.title}" (${item.program_code})?\n\n`
      + 'Esta acción purgará permanentemente todos sus vectores de Qdrant y no podrá recuperarse.',
    );
    if (!confirmed) return;

    setFeedback(`Eliminando "${item.program_code}" y purgando vectores de Qdrant...`);
    try {
      const res = await fetch(`${BACKEND_URL}/knowledge-bases/${item.id}`, {
        method: 'DELETE',
      });
      if (res.ok) {
        setItems((prev) => prev.filter((p) => p.id !== item.id));
        if (selectedProgram?.id === item.id) {
          setSelectedProgram(undefined);
        }
        setFeedback(`🗑️ Programa "${item.program_code}" y vectores de Qdrant eliminados correctamente.`);
        setTimeout(() => setFeedback(undefined), 4000);
      } else {
        setFeedback('Error al eliminar el programa del servidor.');
        setTimeout(() => setFeedback(undefined), 3000);
      }
    } catch {
      setFeedback('Error de conexión al eliminar el programa.');
      setTimeout(() => setFeedback(undefined), 3000);
    }
  });

  const handleToggleStatus = useLastCallback(async (item: KnowledgeBaseItem) => {
    const nextStatus = item.status === 'closed' ? 'indexed' : 'closed';
    try {
      const res = await fetch(`${BACKEND_URL}/knowledge-bases/${item.id}/status`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: nextStatus }),
      });
      if (res.ok) {
        setItems((prev) => prev.map((p) => (p.id === item.id ? { ...p, status: nextStatus } : p)));
        setFeedback(
          `Estado de "${item.program_code}" actualizado a: ${nextStatus === 'closed' ? '🔴 Cerrado' : '🟢 Abierto'}.`,
        );
        setTimeout(() => setFeedback(undefined), 3500);
      }
    } catch {
      setFeedback('Error al alternar estado del programa.');
      setTimeout(() => setFeedback(undefined), 3000);
    }
  });

  const handleSuccessUpload = useLastCallback((newItem: NewKnowledgeBaseItem) => {
    if (!newItem || !newItem.program_code) {
      loadData();
      return;
    }
    setItems((prev) => {
      const validPrev = (prev || []).filter((p): p is KnowledgeBaseItem => Boolean(p && p.program_code && p.status));
      const exists = validPrev.some((p) => p.program_code === newItem.program_code);
      if (exists) {
        return validPrev.map((p) => (p.program_code === newItem.program_code ? { ...p, ...newItem } : p));
      }
      return [newItem, ...validPrev];
    });
    const feedbackTitle = newItem.title || newItem.program_code;
    setFeedback(`✅ "${feedbackTitle}" (${newItem.program_code}) indexado exitosamente en Qdrant.`);
    setTimeout(() => setFeedback(undefined), 4000);
    loadData();
  });

  const handleBannerDragEnter = useLastCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer) {
      e.dataTransfer.dropEffect = 'copy';
    }
    setIsBannerDragOver(true);
  });

  const handleBannerDragOver = useLastCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer) {
      e.dataTransfer.dropEffect = 'copy';
    }
    setIsBannerDragOver(true);
  });

  const handleBannerDragLeave = useLastCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    const relatedTarget = e.relatedTarget as Node | null;
    if (e.currentTarget.contains(relatedTarget)) {
      return;
    }
    setIsBannerDragOver(false);
  });

  const handleBannerDrop = useLastCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsBannerDragOver(false);

    let file: File | undefined = e.dataTransfer.files?.[0];
    if (!file && e.dataTransfer.items?.length) {
      for (let i = 0; i < e.dataTransfer.items.length; i++) {
        const item = e.dataTransfer.items[i];
        if (item.kind === 'file') {
          file = item.getAsFile() || undefined;
          if (file) break;
        }
      }
    }

    if (file) {
      setDroppedFile(file);
      setIsUploadModalOpen(true);
    }
  });

  const handleCloseModal = useLastCallback(() => {
    setIsUploadModalOpen(false);
    setDroppedFile(undefined);
  });

  const validItems = items.filter((it): it is KnowledgeBaseItem => Boolean(it && it.status));

  const filteredItems = validItems.filter((it) => {
    if (filterStatus === 'active') return it.status !== 'closed';
    if (filterStatus === 'closed') return it.status === 'closed';
    return true;
  });

  return (
    <div className={styles.root}>
      <header className={styles.header}>
        <div>
          <h2 className={styles.title}>Base de Conocimiento RAG</h2>
          <p className={styles.subtitle}>
            Documentos curriculares y fragmentos vectoriales indexados en Qdrant para asistencia inteligente.
          </p>
        </div>
        <div className={styles.headerRight}>
          <button
            type="button"
            className={styles.btnUploadHeader}
            onClick={() => setIsUploadModalOpen(true)}
          >
            <Icon name="attach" />
            <span>+ Subir e Indexar Documento</span>
          </button>
        </div>
      </header>

      {/* Zona de Carga de Documentos */}
      <div
        className={buildClassName(styles.dropzone, isBannerDragOver && styles.dragOver)}
        onClick={() => setIsUploadModalOpen(true)}
        onDragEnter={handleBannerDragEnter}
        onDragOver={handleBannerDragOver}
        onDragLeave={handleBannerDragLeave}
        onDrop={handleBannerDrop}
        data-dropzone="true"
        role="button"
        tabIndex={0}
      >
        <span className={styles.dropzoneText}>
          📄 Haz clic aquí para subir e indexar un nuevo documento (.docx, .pdf, .txt, .md o Markdown directo)
        </span>
        <span className={styles.dropzoneHint}>
          Define el tipo (Diplomado, Maestría, Especialidad), si está Activo o Cerrado
          y genera embeddings en Qdrant con BAAI/bge-m3
        </span>
      </div>

      {feedback && (
        <div className={styles.feedbackInfo}>
          {feedback}
        </div>
      )}

      {/* Barra de Filtros */}
      <div className={styles.filterBar}>
        <button
          type="button"
          className={buildClassName(styles.filterTab, filterStatus === 'all' && styles.activeFilter)}
          onClick={() => setFilterStatus('all')}
        >
          Todos (
          {validItems.length}
          )
        </button>
        <button
          type="button"
          className={buildClassName(styles.filterTab, filterStatus === 'active' && styles.activeFilter)}
          onClick={() => setFilterStatus('active')}
        >
          🟢 Convocatoria Abierta (
          {validItems.filter((i) => i.status !== 'closed').length}
          )
        </button>
        <button
          type="button"
          className={buildClassName(styles.filterTab, filterStatus === 'closed' && styles.activeFilter)}
          onClick={() => setFilterStatus('closed')}
        >
          🔴 Cerrados (
          {validItems.filter((i) => i.status === 'closed').length}
          )
        </button>
      </div>

      {/* Grid de Programas Académicos o Estado Vacío */}
      {filteredItems.length === 0 ? (
        <div className={styles.emptyState}>
          <div className={styles.emptyIcon}>📂</div>
          <h3 className={styles.emptyTitle}>Base de Conocimiento Limpia</h3>
          <p className={styles.emptyText}>
            Todos los documentos y vectores en Qdrant han sido purgados exitosamente.
            Haz clic en el botón para subir e indexar tu primer programa académico.
          </p>
          <button
            type="button"
            className={styles.btnUploadHeader}
            onClick={() => setIsUploadModalOpen(true)}
          >
            <Icon name="attach" />
            <span>+ Subir e Indexar Primer Documento RAG</span>
          </button>
        </div>
      ) : (
        <div className={styles.programsGrid}>
          {filteredItems.map((item) => (
            <div key={item.id} className={styles.programCard}>
              <div>
                <div className={styles.cardHeader}>
                  <div className={styles.badgesRow}>
                    <span className={styles.programCode}>{item.program_code}</span>
                    <span className={styles.typeBadge}>{getProgramTypeLabel(item.program_type)}</span>
                  </div>
                  <span className={buildClassName(styles.statusBadge, item.status === 'closed' && styles.closed)}>
                    {item.status === 'closed' ? '🔴 Cerrado' : '🟢 Activo'}
                  </span>
                </div>
                <h4 className={styles.programTitle}>{item.title}</h4>
                <div className={styles.cardMeta}>
                  <span>
                    Fragmentos:
                    {' '}
                    <strong>{item.total_chunks}</strong>
                  </span>
                  <span>Motor: Qdrant</span>
                </div>
              </div>

              <div className={styles.cardActionRows}>
                <div className={styles.cardActions}>
                  <button
                    type="button"
                    className={styles.actionButton}
                    onClick={() => handleSelectProgramChunks(item)}
                  >
                    Ver Chunks
                  </button>
                  <button
                    type="button"
                    className={buildClassName(styles.actionButton, styles.primary)}
                    onClick={() => handleReindex(item.id)}
                  >
                    Re-indexar
                  </button>
                </div>
                <div className={styles.cardActions}>
                  <button
                    type="button"
                    className={buildClassName(styles.actionButton, styles.statusToggle)}
                    onClick={() => handleToggleStatus(item)}
                    title={item.status === 'closed' ? 'Cambiar a convocatoria abierta' : 'Cambiar a programa cerrado'}
                  >
                    {item.status === 'closed' ? '🟢 Abrir' : '🔴 Cerrar'}
                  </button>
                  <button
                    type="button"
                    className={buildClassName(styles.actionButton, styles.danger)}
                    onClick={() => handleDeleteProgram(item)}
                    title="Eliminar programa y purgar vectores de Qdrant"
                  >
                    <Icon name="delete" />
                    <span>Eliminar</span>
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Visor de Chunks Vectoriales de Qdrant */}
      {selectedProgram && (
        <div className={styles.chunksModal}>
          <div className={styles.chunksHeader}>
            <h4 className={styles.chunksTitle}>
              Fragmentos Vectoriales en Qdrant:
              {' '}
              {selectedProgram.program_code}
            </h4>
            <button
              type="button"
              className={buildClassName(styles.actionButton, styles.closeButton)}
              onClick={() => setSelectedProgram(undefined)}
            >
              Cerrar
            </button>
          </div>

          <div className={styles.chunksList}>
            {chunks.map((chunk, idx) => (
              <div key={chunk.id || idx} className={styles.chunkItem}>
                <div className={styles.chunkMeta}>
                  <span>
                    CHUNK #
                    {idx + 1}
                    {' '}
                    (
                    {chunk.program_code}
                    )
                  </span>
                  <span>
                    SIMILITUD:
                    {' '}
                    {chunk.metadata?.similarity ? `${Math.round(chunk.metadata.similarity * 100)}%` : '92%'}
                  </span>
                </div>
                <div>{chunk.content}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Modal de Subida e Indexación Avanzada */}
      <UploadKnowledgeModal
        isOpen={isUploadModalOpen}
        onClose={handleCloseModal}
        onSuccess={handleSuccessUpload}
        initialFile={droppedFile}
      />
    </div>
  );
};

export default memo(KnowledgeBaseView);
